import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  PlusCircle,
  FileText,
  Hotel,
  Route,
  Car,
  MapPin,
  Users,
  Settings,
  LogOut,
  Building2,
  Menu,
  X,
  Truck,
  UserCheck,
  Calendar,
  Users2,
  DollarSign,
  CreditCard,
  Lock,
  Database,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import MobileNav from './layout/MobileNav';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isB2BAdmin } = useAuth();
  const { tenant, tenantSlug, buildPath } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const handleLogout = () => {
    logout();
    navigate(buildPath('/login'));
  };

  // Check if any settings sub-item is active
  const isSettingsActive = () => {
    const settingsPaths = ['/admin/settings', '/admin/users', '/admin/suppliers', '/subscription'];
    return settingsPaths.some(p => location.pathname.includes(p));
  };

  // Build tenant-aware paths - Reordered as per user request
  const menuItems = [
    { path: buildPath('/dashboard'), label: 'Dashboard', icon: LayoutDashboard, show: true },
    { path: buildPath('/quotations/create'), label: 'Create Quotation', icon: PlusCircle, show: true },
    { path: buildPath('/quotations'), label: 'All Quotations', icon: FileText, show: true },
    { path: buildPath('/ai-quotes'), label: 'Instant Quote via AI', icon: Sparkles, show: true },
    { path: buildPath('/sub-users'), label: 'Team Members', icon: Users, show: isB2BAdmin },
    { divider: true, show: isAdmin },
    { path: buildPath('/admin/places'), label: 'Places', icon: MapPin, show: isAdmin },
    { path: buildPath('/admin/routes'), label: 'Routes', icon: Route, show: isAdmin },
    { path: buildPath('/admin/itineraries'), label: 'Itinerary Master', icon: Calendar, show: isAdmin },
    { path: buildPath('/admin/hotels'), label: 'Hotels', icon: Hotel, show: isAdmin },
    { path: buildPath('/admin/hotel-rates'), label: 'Hotel Rates', icon: DollarSign, show: isAdmin },
    { path: buildPath('/admin/transfers'), label: 'Transfers', icon: Car, show: isAdmin },
    { path: buildPath('/admin/vehicles'), label: 'Vehicle Master', icon: Truck, show: isAdmin },
    { path: buildPath('/admin/masters'), label: 'Masters', icon: Database, show: isAdmin },
    { path: buildPath('/admin/travel-agents'), label: 'B2B Agents', icon: UserCheck, show: isAdmin },
    { divider: true, show: isAdmin },
  ];

  // Settings sub-items
  const settingsSubItems = [
    { path: buildPath('/admin/settings'), label: 'General Settings', icon: Settings, show: isAdmin },
    { path: buildPath('/admin/users'), label: 'Users', icon: Users, show: isAdmin },
    { path: buildPath('/admin/suppliers'), label: 'Suppliers', icon: Truck, show: isAdmin },
    { path: buildPath('/subscription'), label: 'Subscription', icon: CreditCard, show: isAdmin },
  ];

  const isActive = (path) => location.pathname === path;

  const handleMenuClick = (path) => {
    setMobileMenuOpen(false);
    if (path) navigate(path);
  };

  // Get tenant branding - use tenant data from context
  const companyName = tenant?.company_name || tenantSlug?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'OnTrOf';
  const logoUrl = tenant?.logo_url;

  const Sidebar = ({ mobile = false }) => (
    <aside
      className={`bg-white border-r border-[rgba(10,31,68,0.08)] flex flex-col ${
        mobile ? 'w-full h-full' : 'w-64 hidden lg:flex'
      }`}
      data-testid="sidebar"
    >
      {/* Logo/Brand Section */}
      <div className="p-5 border-b border-[rgba(10,31,68,0.08)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-10 w-10 object-contain rounded-lg" />
            ) : (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-chrome-fade">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-base font-brand font-bold text-brand truncate max-w-[140px]">{companyName}</h1>
              <p className="text-xs text-muted-brand font-ui">{tenantSlug || 'Travel Office'}</p>
            </div>
          </div>
          {mobile && (
            <button onClick={() => setMobileMenuOpen(false)} className="lg:hidden text-brand">
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {menuItems.map((item, index) => {
          if (!item.show) return null;

          if (item.divider) {
            return <div key={index} className="border-t border-[rgba(10,31,68,0.08)] my-3" />;
          }

          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => handleMenuClick(item.path)}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all text-left font-ui ${
                isActive(item.path)
                  ? 'bg-blue-50 text-blue-700 font-semibold border-l-4 border-blue-600 rounded-l-none'
                  : 'text-muted-brand hover:bg-slate-50 hover:text-brand'
              }`}
              data-testid={`nav-item-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${isActive(item.path) ? 'text-blue-600' : ''}`} />
              <span className="text-sm">{item.label}</span>
            </button>
          );
        })}
        
        {/* Settings with expandable sub-menu */}
        {isAdmin && (
          <div>
            <button
              onClick={() => setSettingsExpanded(!settingsExpanded)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all text-left font-ui ${
                isSettingsActive()
                  ? 'bg-blue-50 text-blue-700 font-semibold border-l-4 border-blue-600 rounded-l-none'
                  : 'text-muted-brand hover:bg-slate-50 hover:text-brand'
              }`}
              data-testid="nav-item-settings"
            >
              <div className="flex items-center space-x-3">
                <Settings className={`w-5 h-5 flex-shrink-0 ${isSettingsActive() ? 'text-blue-600' : ''}`} />
                <span className="text-sm">Settings</span>
              </div>
              {settingsExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
            
            {settingsExpanded && (
              <div className="ml-4 mt-1 space-y-0.5">
                {settingsSubItems.map((subItem) => {
                  if (!subItem.show) return null;
                  const SubIcon = subItem.icon;
                  return (
                    <button
                      key={subItem.path}
                      onClick={() => handleMenuClick(subItem.path)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all text-left font-ui ${
                        isActive(subItem.path)
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-muted-brand hover:bg-slate-50 hover:text-brand'
                      }`}
                      data-testid={`nav-item-${subItem.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <SubIcon className={`w-4 h-4 flex-shrink-0 ${isActive(subItem.path) ? 'text-blue-600' : ''}`} />
                      <span className="text-sm">{subItem.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-[rgba(10,31,68,0.08)]">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-10 h-10 bg-chrome-pale rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-chrome font-brand font-semibold text-sm">
              {user?.full_name?.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-brand font-ui truncate">{user?.full_name}</p>
            <p className="text-xs text-muted-brand font-ui truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start mb-1 text-muted-brand hover:text-brand hover:bg-off font-ui"
          onClick={() => navigate(buildPath('/change-password'))}
          data-testid="change-password-button"
        >
          <Lock className="w-4 h-4 mr-2" />
          Change Password
        </Button>
        <Button
          variant="outline"
          className="w-full justify-start border-[rgba(10,31,68,0.08)] text-muted-brand hover:text-brand hover:bg-off font-ui"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-off" data-testid="layout">
      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-brand/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-[60] transform transition-transform duration-300 ease-in-out lg:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar mobile />
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-[rgba(10,31,68,0.08)] px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileMenuOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-6 h-6 text-brand" />
          </button>
          <div className="flex items-center space-x-2">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-8 w-8 object-contain rounded-lg" />
            ) : (
              <div className="w-8 h-8 bg-chrome-fade rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-brand font-bold text-brand truncate max-w-[120px]">{companyName}</span>
          </div>
          <div className="w-6" />
        </div>

        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 app-mobile-content">{children}</div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileNav />
    </div>
  );
};

export default Layout;
